"use client";

import type { RefObject } from "react";
import { useRef } from "react";

import type { CAROUSEL_GRID_SIZES } from "../constants/carouselGridSizes.constants";
import type { CarouselGridSize } from "../types/carouselGridSize.types";
import { useIntersectionObserverRefState } from "./useIntersectionObserverRefState";

export type CarouselApi = {
  canScrollNext: boolean;
  canScrollPrevious: boolean;
  endIsVisible: boolean;
  fixedWidth?: boolean;
  forceMount: boolean;
  gap?: [number, number];
  hoverButtons: boolean;
  pageButtons: boolean;
  pageOffset: [[number, number], [number, number]];
  refEnd: RefObject<HTMLDivElement>;
  refParent: RefObject<HTMLDivElement>;
  refStart: RefObject<HTMLDivElement>;
  scrollNext: () => void;
  scrollPrevious: () => void;
  size: keyof typeof CAROUSEL_GRID_SIZES | CarouselGridSize;
  startIsVisible: boolean;
};

export type UseCarouselProps = {
  fixedWidth?: boolean;
  forceMount?: boolean;
  gap?: CarouselApi["gap"];
  hoverButtons?: boolean;
  pageOffset?: CarouselApi["pageOffset"];
} & (
  | {
      size?: Omit<CarouselApi["size"], "xl">;
      showPageButtons?: never;
    }
  | {
      size?: "xl";
      showPageButtons?: boolean;
    }
);

export const useCarousel = ({
  hoverButtons = true,
  gap = [4, 8],
  pageOffset = [
    [24, 48],
    [24, 48],
  ],
  size = "m",
  fixedWidth = true,
  showPageButtons = false,
  forceMount = false,
}: UseCarouselProps): CarouselApi => {
  const refEnd = useRef<HTMLDivElement>(null);
  const refStart = useRef<HTMLDivElement>(null);
  const refParent = useRef<HTMLDivElement>(null);

  const [endIsVisible] = useIntersectionObserverRefState({
    deps: [pageOffset, size],
    ref: refEnd,
  });
  const [startIsVisible] = useIntersectionObserverRefState({
    deps: [pageOffset, size],
    ref: refStart,
  });

  const canScrollPrevious = !startIsVisible;
  const canScrollNext = !endIsVisible;

  const scrollNext = () => {
    refParent?.current?.scrollTo({
      left: refParent.current.scrollLeft + refParent.current.clientWidth,
      behavior: "smooth",
    });
  };

  const scrollPrevious = () => {
    refParent?.current?.scrollTo({
      left: refParent.current.scrollLeft - refParent.current.clientWidth,
      behavior: "smooth",
    });
  };

  return {
    canScrollNext,
    canScrollPrevious,
    endIsVisible,
    fixedWidth,
    forceMount,
    gap,
    hoverButtons,
    pageButtons: showPageButtons,
    pageOffset,
    refEnd,
    refParent,
    refStart,
    scrollNext,
    scrollPrevious,
    size: size as CarouselApi["size"],
    startIsVisible,
  };
};
